import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { X } from "lucide-react";
import { Navigation } from "../components/Navigation";
import { Footer } from "../components/Footer";
import { API, imageUrl } from "../config";
import { useLang } from "../i18n";

const FSC_CERTIFICATE_ID = "785c8dc9-56c9-4c05-8ffc-0bc116fb6a41";
const FSC_LICENSE = "FSC™ C123954";

export default function Sertifikalar() {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const { t, dn } = useLang();

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/certificates`)
      .then((res) => setCerts(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="sertifikalar-page" className="bg-white">
      <Navigation />

      <section className="bg-[#0D0C0B] pt-32 pb-12 md:pt-40 md:pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-[#7BA05B]" />
            <span className="font-sans uppercase tracking-[0.3em] text-[10px] text-[#C67D57]">İnter Orman</span>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl tracking-tighter text-[#F2EFEB] leading-none">{dn("Sertifikalar")}</h1>
        </div>
      </section>

      <section className="pt-12 pb-20 md:pt-16 md:pb-28 max-w-7xl mx-auto px-6 md:px-12 min-h-[60vh]">
        {loading ? (
          <div className="text-[#6B6560] font-sans uppercase tracking-[0.2em] text-xs">{t("common.loading")}</div>
        ) : certs.length === 0 ? (
          <div data-testid="certificates-empty" className="text-[#6B6560] py-20 text-center border border-black/10 rounded-lg">
            {t("certs.empty")}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {certs.map((c, i) => (
              <motion.button
                key={c.id}
                data-testid={`certificate-${c.id}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                onClick={() => setActive(c)}
                className="group text-left self-start min-w-0"
              >
                <div className="relative overflow-hidden aspect-[3/4] bg-[#F0EDE8] border border-black/10">
                  <img
                    src={imageUrl(c.image)}
                    alt={c.title || "Sertifika"}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0D0C0B]/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
                {c.id === FSC_CERTIFICATE_ID && (
                  <p data-testid="fsc-certificate-license" translate="no" className="notranslate mt-4 text-center font-sans text-lg md:text-xl font-semibold tracking-normal text-[#1A1715]">
                    {FSC_LICENSE}
                  </p>
                )}
              </motion.button>
            ))}
          </div>
        )}
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="certificate-lightbox"
            className="fixed inset-0 z-[70] flex flex-col items-center justify-center p-6 bg-black/85 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
          >
            <button
              data-testid="lightbox-close"
              onClick={() => setActive(null)}
              aria-label={t("common.close")}
              className="absolute top-6 right-6 z-10 text-white hover:text-[#C67D57] transition-colors"
            >
              <X size={28} strokeWidth={1.5} />
            </button>
            <motion.img
              data-testid="certificate-lightbox-image"
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.3 }}
              src={imageUrl(active.image)}
              alt={active.title || "Sertifika"}
              onClick={(e) => e.stopPropagation()}
              className={`max-w-full object-contain rounded-lg ${active.id === FSC_CERTIFICATE_ID ? "max-h-[75vh]" : "max-h-[85vh]"}`}
            />
            {active.id === FSC_CERTIFICATE_ID && (
              <p data-testid="fsc-certificate-lightbox-license" translate="no" onClick={(e) => e.stopPropagation()} className="notranslate mt-4 text-center font-sans text-lg md:text-xl font-semibold tracking-normal text-white">
                {FSC_LICENSE}
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
